import type { DataSource } from '../../types/siteRag';
import type { SourceRoleSed, SourceRoleTruthFinding } from './sourceRoleTruthGuard';
import { buildNumericSourceEvidence } from '../grounding/numericSourceEvidence';

const escape = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Only the exact cited inference-only research identity and raw HQ distance
 * can qualify this location phrase. No new distances or tribal territories. */
function mismatches(text: string, sed: SourceRoleSed, references: readonly DataSource[]): Array<{ sentence: string; phrase: string; replacement: string; refId: number }> {
  const rows = buildNumericSourceEvidence({ references, frozenSiteResearch: sed.siteResearch }).tribalHeadquartersDistances ?? [];
  const matches: Array<{ sentence: string; phrase: string; replacement: string; refId: number }> = [];
  for (const sentence of text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n+|;\s*|,?\s+(?:but|whereas)\s+/i)) {
    if (/\?\s*$/.test(sentence) || /\b(?:if|whether|hypothetical|not true|incorrect|cannot establish)\b/i.test(sentence)
      || /\b(?:peer|comparable|other|another)\s+(?:project|site|campus)\b/i.test(sentence)) continue;
    const refs = [...sentence.matchAll(/\[Ref\s+(\d+(?:\s*,\s*(?:Ref\s+)?\d+)*)\]/gi)].flatMap(match => (match[1]!.match(/\d+/g) ?? []).map(Number));
    for (const row of rows) {
      if (!refs.includes(row.refId)) continue;
      const pattern = new RegExp(`\\b${escape(row.entity)}\\s+(?:is\\s+)?located\\s+(?:approximately\\s+|about\\s+)?${escape(String(row.value))}\\s+${row.unit}\\s+away\\b`, 'i');
      const match = pattern.exec(sentence);
      if (!match) continue;
      matches.push({ sentence, phrase: match[0], replacement: `${row.entity}, whose headquarters screening research places approximately ${row.value} ${row.unit} away`, refId: row.refId });
    }
  }
  return matches;
}

export function tribalHeadquartersSourceScopeFindings(text: string, sed: SourceRoleSed, sectionId: string, references: readonly DataSource[]): SourceRoleTruthFinding[] {
  return mismatches(text, sed, references).map(row => { const expected = 'The cited screening research describes a tribal headquarters distance, not the location of all tribal members, tribal territory, project interest or a consultation requirement. Retain the named headquarters and research attribution; consultation applicability requires its own federal-nexus assessment.';
    return { kind: 'tribal-headquarters-scope', severity: 'block', sectionId, excerpt: row.sentence, observed: row.phrase,
      expected, message: expected, sourcePaths: ['siteResearch.data.tribalLands.nearbyTribes', 'references'] }; });
}

export function repairTribalHeadquartersSourceScope(text: string, sed: SourceRoleSed, references: readonly DataSource[]): string | null {
  const rows = mismatches(text, sed, references);
  if (!rows.length) return null;
  let result = text;
  for (const row of rows) result = result.replace(row.sentence, row.sentence.replace(row.phrase, row.replacement));
  return result;
}
